import { useCallback, useEffect, useState, useRef } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import Select from "react-select";
import APIs from "../../Constants/apis.js";
import swal from "sweetalert";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import PageSizeDropDownSelection, {
  SortOrderDropDownSelection,
} from "../../components/CustomComponents/DropdownSelections/DropDownSelections.jsx";
import { logout } from "../../app/auth/authSlice.js";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import { setVisibleColumns, setSortBy, setSortOrder } from "../../app/itemGridPreferencesSlice.js";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils.js";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import AddUpdateAllergenModal from "./add&updateAllergenModal.jsx";
import "./AllergenFilters.css";

const ManageAllergens = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const pageKey = "allergens";
  const defaultVisibleColumns = {
    name: true,
    description: true,
    sortOrder: true,
  };
  const defaultSortOrder = { value: "ASC", label: "Asc" };
  const defaultSortBy = { value: "Allergen_SortOrder", label: "Sort Order" };
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;

  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState({ value: "50", label: "50" });
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [allergens, setAllergens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAllergens, setSelectedAllergens] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [allergenToEdit, setAllergenToEdit] = useState(null);

  // Drag and drop refs
  const dragItem = useRef();
  const dragOverItem = useRef();

  const sortByOptions = [
    { value: "ID", label: "ID" },
    { value: "Allergen_Name", label: "Name" },
    { value: "Allergen_Description", label: "Description" },
    { value: "Allergen_SortOrder", label: "Sort Order" },
  ];

  const FetchAllergens = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_ALLERGENS, {
        params: {
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          Search: search.trim() || "",
          PageSize: pageSize.value,
          PageNumber: pageNumber,
        },
      });

      if (response.data.status) {
        const allergenData = response.data.data;

        if (Array.isArray(allergenData)) {
          setAllergens(allergenData);
          setTotalRecords(response.data.totalRecords || 0);
          setTotalPages(response.data.totalPages || 1);
        } else {
          console.error("Unexpected API response format:", response.data);
          setAllergens([]);
          setTotalRecords(0);
          setTotalPages(1);
        }
      } else {
        showErrorToast(response.data.Message || "Failed to fetch allergens");
        setAllergens([]);
        setTotalRecords(0);
        setTotalPages(1);
      }

      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      if (err.response && err.response.status === 401) {
        dispatch(logout());
        setTimeout(() => {
          navigate("/user/login", { replace: true });
        }, 1000);
      } else {
        showErrorToast("Failed to fetch allergens");
      }
    }
  }, [search, sortBy, sortOrder, pageSize, pageNumber, dispatch, navigate]);

  useEffect(() => {
    FetchAllergens();
  }, [FetchAllergens]);

  // Initialize preferences on mount
  useEffect(() => {
    // Check if preferences exist and merge with defaults to ensure new columns are included
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      // Check if preferences need updating (missing new columns or have removed columns)
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        // Merge existing preferences with default columns (this handles both additions and removals)
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    // eslint-disable-next-line
  }, [dispatch, pageKey]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPageNumber(1);
  };

  const handlePageSizeChange = (selectedOption) => {
    setPageSize(selectedOption);
    setPageNumber(1);
  };

  const handleSortByChange = (selectedOption) => {
    dispatch(setSortBy({ pageKey, sortBy: selectedOption }));
    setPageNumber(1);
  };

  const handleSortOrderChange = (selectedOption) => {
    dispatch(setSortOrder({ pageKey, sortOrder: selectedOption }));
    setPageNumber(1);
  };

  const handlePageChange = (page) => {
    setPageNumber(page);
  };

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      setSelectedAllergens(allergens.map((allergen) => allergen.id));
    } else {
      setSelectedAllergens([]);
    }
  };

  const handleSelectAllergen = (allergenId, e) => {
    const checked = e.target.checked;
    if (checked) {
      setSelectedAllergens([...selectedAllergens, allergenId]);
    } else {
      setSelectedAllergens(selectedAllergens.filter((id) => id !== allergenId));
    }
  };

  const handleDeleteAllergen = async (allergenId) => {
    try {
      const result = await swal({
        title: "Are you sure?",
        text: "Once deleted, you will not be able to recover this allergen!",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (result) {
        setLoading(true);
        const response = await axiosInstance.delete(APIs.DELETE_ALLERGEN, {
          params: {
            ID: allergenId,
          },
        });

        if (response.data.status) {
          showSuccessToast("Allergen deleted successfully");
          FetchAllergens();
        } else {
          showErrorToast(response.data.Message || "Failed to delete allergen");
        }
        setLoading(false);
      }
    } catch (err) {
      console.log(err);
      setLoading(false);
      if (err.response && err.response.status === 401) {
        dispatch(logout());
        setTimeout(() => {
          navigate("/user/login", { replace: true });
        }, 1000);
      } else {
        showErrorToast("Failed to delete allergen");
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedAllergens.length === 0) {
      showErrorToast("Please select allergens to delete");
      return;
    }

    try {
      const result = await swal({
        title: "Are you sure?",
        text: `This will delete ${selectedAllergens.length} allergen(s). Once deleted, you will not be able to recover them!`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (result) {
        setLoading(true);
        let successCount = 0;
        let failCount = 0;

        for (const allergenId of selectedAllergens) {
          try {
            const response = await axiosInstance.delete(APIs.DELETE_ALLERGEN, {
              params: {
                ID: allergenId,
              },
            });

            if (response.data.status) {
              successCount++;
            } else {
              failCount++;
            }
          } catch (err) {
            failCount++;
          }
        }

        if (successCount > 0) {
          showSuccessToast(`${successCount} allergen(s) deleted successfully`);
        }
        if (failCount > 0) {
          showErrorToast(`Failed to delete ${failCount} allergen(s)`);
        }

        setSelectedAllergens([]);
        setSelectAll(false);
        FetchAllergens();
        setLoading(false);
      }
    } catch (err) {
      console.log(err);
      setLoading(false);
      showErrorToast("Failed to delete allergens");
    }
  };


  const handleEditAllergen = (allergen) => {
    setAllergenToEdit(allergen);
    setShowEditModal(true);
  };

  const handleAllergenSubmit = async (allergenData, isEdit, isBulkEdit) => {
    try {
      setLoading(true);
      let response;

      if (isEdit) {
        response = await axiosInstance.put(APIs.PUT_UPDATE_ALLERGEN, {
          ID: allergenData.id,
          Allergen_Name: allergenData.name.trim(),
          Allergen_Description: allergenData.description.trim(),
          Allergen_SortOrder: allergenData.sortOrder || 0,
          CompID: 0, // SuperAdmin can set CompID to 0 or specific company
        });
      } else {
        response = await axiosInstance.post(APIs.POST_INSERT_ALLERGEN, {
          Allergen_Name: allergenData.name.trim(),
          Allergen_Description: allergenData.description.trim(),
          Allergen_SortOrder: allergenData.sortOrder || 0,
          CompID: 0, // SuperAdmin can set CompID to 0 or specific company
        });
      }

      if (response.data.status) {
        showSuccessToast(
          isEdit ? "Allergen updated successfully" : "Allergen added successfully"
        );
        setShowAddModal(false);
        setShowEditModal(false);
        setAllergenToEdit(null);
        FetchAllergens();
      } else {
        showErrorToast(response.data.Message || "Failed to save allergen");
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      if (err.response && err.response.status === 401) {
        dispatch(logout());
        setTimeout(() => {
          navigate("/user/login", { replace: true });
        }, 1000);
      } else {
        showErrorToast("Failed to save allergen");
      }
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    dragItem.current = index;
    e.target.style.opacity = "0.5";
  };

  const handleDragEnter = (e, index) => {
    dragOverItem.current = index;
    e.target.style.backgroundColor = "#e3f2fd";
  };

  const handleDragLeave = (e) => {
    e.target.style.backgroundColor = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const dragIndex = dragItem.current;
    const dragOverIndex = dragOverItem.current;

    if (dragIndex !== dragOverIndex) {
      const newAllergens = [...allergens];
      const draggedItem = newAllergens[dragIndex];
      newAllergens.splice(dragIndex, 1);
      newAllergens.splice(dragOverIndex, 0, draggedItem);

      setAllergens(newAllergens);

      // Update sort order in backend
      try {
        const updates = newAllergens.map((allergen, index) => ({
          ID: allergen.id,
          SortOrder: index + 1,
        }));

        const response = await axiosInstance.post(APIs.POST_UPDATE_ALLERGEN_SORT_ORDER, {
          Updates: updates,
        });

        if (response.data.status) {
          showSuccessToast("Sort order updated successfully");
        } else {
          showErrorToast("Failed to update sort order");
          // Revert the change
          FetchAllergens();
        }
      } catch (err) {
        console.log(err);
        showErrorToast("Failed to update sort order");
        // Revert the change
        FetchAllergens();
      }
    }

    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragEnd = (e) => {
    e.target.style.backgroundColor = "";
    e.target.style.opacity = "1";
  };


  return (
    <div className="content">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">
                    <Link to="/dashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">Manage Allergens</li>
                </ol>
                <h1 className="page-header mb-0">Manage Allergens</h1>
              </div>
              <div className="ms-auto">
                <button
                  className="btn px-4"
                  onClick={() => setShowAddModal(true)}
                  style={{
                    backgroundColor: themeColor,
                    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    border: "none",
                    color: "white",
                    borderRadius: "25px",
                    fontWeight: "500"
                  }}
                >
                  <i className="fa fa-plus fa-lg me-2 ms-n2"></i>
                  Add Allergen
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                {/* Enhanced Filter Section */}
                <div className="mb-4">
                  <div
                    className="card filter-card"
                    style={{
                      position: "relative",
                      zIndex: 1,
                      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                      borderRadius: '12px',
                      border: 'none',
                      boxShadow: `0 4px 20px ${themeColor}40`
                    }}
                  >
                    <div className="card-body p-3">
                      {/* Filter Controls */}
                      <div className="row g-3 align-items-center">
                        {/* Search Field */}
                        <div className="col-xl-4 col-lg-4 col-md-6">
                          <div className="search-input filter-control">
                            <input
                              type="text"
                              className="filter-input"
                              value={search}
                              onChange={handleSearch}
                              placeholder="Search allergens..."
                              style={{
                                backgroundColor: isDarkMode ? 'rgba(45, 52, 54, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                                border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(255, 255, 255, 0.3)',
                                color: isDarkMode ? '#ffffff' : '#333',
                                borderRadius: '6px',
                                height: '36px'
                              }}
                            />
                          </div>
                        </div>

                        {/* Sort By Dropdown */}
                        <div className="col-xl-3 col-lg-3 col-md-6">
                          <div className="filter-control">
                            <Select
                              value={sortBy}
                              onChange={handleSortByChange}
                              options={sortByOptions}
                              styles={isDarkMode ? DropDownStylesDark : DropDownStyles}
                              placeholder="Sort By"
                              className="filter-dropdown"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              menuShouldBlockScroll={false}
                              menuShouldScrollIntoView={false}
                              closeMenuOnScroll={false}
                            />
                          </div>
                        </div>

                        {/* Sort Order Dropdown */}
                        <div className="col-xl-2 col-lg-2 col-md-6">
                          <div className="filter-control">
                            <SortOrderDropDownSelection
                              defaultValue={sortOrder}
                              onChange={handleSortOrderChange}
                              styles={isDarkMode ? DropDownStylesDark : DropDownStyles}
                            />
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="col-xl-3 col-lg-3 col-md-12">
                          <div className="d-flex gap-2">
                            <button
                              className="clear-btn"
                              onClick={() => {
                                setSearch("");
                                setPageNumber(1);
                              }}
                              title="Clear all filters"
                              style={{
                                height: "32px",
                                backgroundColor: isDarkMode ? 'rgba(45, 52, 54, 0.8)' : 'rgba(255, 255, 255, 0.2)',
                                border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(255, 255, 255, 0.3)',
                                color: 'white',
                                borderRadius: '6px'
                              }}
                            >
                              <i className="fa fa-times me-1"></i>
                              Filter
                            </button>

                            <ColumnSelection
                              visibleColumns={visibleColumns}
                              setVisibleColumns={(cols) =>
                                dispatch(setVisibleColumns({ pageKey, columns: cols }))
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Selection Info and Actions (Shown only when allergens are selected) */}
                {selectedAllergens.length > 0 && (
                  <div className="mb-3">
                    <div 
                      className="p-3 rounded d-flex align-items-center justify-content-between" 
                      style={{ 
                        backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                        border: isDarkMode ? "1px solid #404040" : "1px solid #dee2e6"
                      }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <span className="fw-bold" style={{ color: themeColor }}>
                          {selectedAllergens.length} allergen{selectedAllergens.length > 1 ? 's' : ''} selected
                        </span>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => {
                            setSelectedAllergens([]);
                            setSelectAll(false);
                          }}
                        >
                          Clear Selection
                        </button>
                      </div>

                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={handleBulkDelete}
                        >
                          <i className="fa fa-trash me-1"></i>Bulk Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}


                <div
                  className="table-responsive mt-3"
                  style={{
                    minHeight: "200px",
                    maxHeight: "450px",
                    overflowY: "auto",
                    overflowX: "auto",
                  }}
                >
                  <table className="table table-hover">
                    <thead
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 5,
                        backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                        color: isDarkMode ? "#ffffff" : "#000000",
                      }}
                    >
                      <tr>
                        <th className="pt-2 pb-2" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>
                          <input
                            type="checkbox"
                            onChange={handleSelectAll}
                            checked={
                              allergens.length > 0 &&
                              selectedAllergens.length === allergens.length
                            }
                          />
                        </th>
                        <th className="pt-2 pb-2" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Name</th>
                        {visibleColumns.description && (
                          <th className="pt-2 pb-2" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Description</th>
                        )}
                        {visibleColumns.sortOrder && (
                          <th className="pt-2 pb-2 text-center" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Sort Order</th>
                        )}
                        <th className="pt-2 pb-2 text-center" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="5" className="text-center">
                            <Preloader />
                          </td>
                        </tr>
                      ) : allergens.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center">
                            No allergens found
                          </td>
                        </tr>
                      ) : (
                        allergens.map((allergen, index) => (
                          <tr
                            key={allergen.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragEnter={(e) => handleDragEnter(e, index)}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onDragEnd={handleDragEnd}
                          >
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedAllergens.includes(allergen.id)}
                                onChange={(e) => handleSelectAllergen(allergen.id, e)}
                              />
                            </td>
                            <td>{allergen.allergen_Name}</td>
                            {visibleColumns.description && (
                              <td>{allergen.allergen_Description || "-"}</td>
                            )}
                            {visibleColumns.sortOrder && (
                              <td className="text-center">{allergen.allergen_SortOrder}</td>
                            )}
                            <td className="text-center">
                              <div className="d-flex justify-content-center gap-2">
                                {/* Drag Handle */}
                                <i
                                  className="fas fa-bars text-secondary"
                                  style={{
                                    cursor: "grab",
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '6px',
                                    border: '1px solid #6c757d',
                                    backgroundColor: 'transparent',
                                    transition: 'all 0.2s ease'
                                  }}
                                  draggable
                                  title="Drag to reorder"
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#6c757d';
                                    e.currentTarget.style.color = 'white';
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = '#6c757d';
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}
                                ></i>

                                {/* Edit Button */}
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '6px',
                                    border: `1px solid ${themeColor}`,
                                    color: themeColor,
                                    transition: 'all 0.2s ease',
                                    padding: 0
                                  }}
                                  onClick={() => handleEditAllergen(allergen)}
                                  title="Edit Allergen"
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = themeColor;
                                    e.currentTarget.style.color = 'white';
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = themeColor;
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}
                                >
                                  <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                                </button>

                                {/* Delete Button */}
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '6px',
                                    border: '1px solid #dc3545',
                                    color: '#dc3545',
                                    transition: 'all 0.2s ease',
                                    padding: 0
                                  }}
                                  onClick={() => handleDeleteAllergen(allergen.id)}
                                  title="Delete Allergen"
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#dc3545';
                                    e.currentTarget.style.color = 'white';
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = '#dc3545';
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}
                                >
                                  <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="row mt-3">
                    <div className="col-12">
                      <Pagination
                        currentPage={pageNumber}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  </div>
                )}

                {/* Records Info */}
                <div className="row mt-3">
                  <div className="col-12">
                    <p style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
                      Showing {allergens.length} of {totalRecords} allergens
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AddUpdateAllergenModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSubmit={handleAllergenSubmit}
        isEdit={false}
        allergen={null}
      />

      <AddUpdateAllergenModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setAllergenToEdit(null);
        }}
        onSubmit={handleAllergenSubmit}
        isEdit={true}
        allergen={allergenToEdit}
      />

    </div>
  );
};

export default ManageAllergens;
